import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
//import Cookies from 'js-cookie';
import { apiConfig } from './apiConfig';
import { msalConfig, tokenRequest } from './authConfig';
import { b2cPolicies } from './policies';

// Crear la instància principal de myMSALObj
// els paràmetres de configuració estan ubicats a authConfig.js
const myMSALObj = new PublicClientApplication(msalConfig);

let accountId = '';
let username = '';
let accessToken = null;

async function getAccountFromRedirectPromise() {
    let account;
    let response;
    try {
        response = await myMSALObj.handleRedirectPromise();
    } finally {
        if (response) {
            /**
             * Per establir un compte actiu per a l'actualització de la interfície, volem considerar només la resposta d'autenticació resultant
             * del flux SUSI. La reclamació "tfp" al token d'identificació ens indica la política (NOTA: les polítiques antigues poden usar "acr" en lloc de "tfp").
             * Per obtenir més informació sobre els tokens B2C, visiteu https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
             */
            if (
                b2cPolicies.authorities.signUpSignInCP.authority
                    .toUpperCase()
                    .includes(response.idTokenClaims['acr'].toUpperCase())
            ) {
                account = response.account;
            }
        }
    }
    return account;
}

async function getAcquireTokenSilentPromise() {
    // MSAL.js v2 exposa diverses APIs de compte, la lògica per determinar quin compte utilitzar és responsabilitat del desenvolupador
    const account = myMSALObj.getAllAccounts()[0];

    const accessTokenRequest = {
        scopes: [...apiConfig.b2cScopes],
        forceRefresh: true,
        account: account
    };

    try {
        let accessTokenResponse = await myMSALObj.acquireTokenSilent(accessTokenRequest);
        accessToken = accessTokenResponse.accessToken;
    } catch (error) {
        console.log('acquireTokenSilent error ', error);
        signOut();
    }
    return accessToken;
}

async function selectAccount() {
    /**
     * Consulteu aquí per obtenir més informació sobre la recuperació de comptes:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */

    // La lògica de selecció de compte depèn de l'aplicació. Ajusteu segons sigui necessari per a diferents casos d'ús.
    // Establir el compte actiu en carregar la pàgina
    let myAccount = await getAccountFromRedirectPromise();
    if (!(myAccount && myAccount.tenantId !== '')) {
        const currentAccounts = myMSALObj.getAllAccounts();
        if (currentAccounts.length < 1) {
            return;
        } else if (currentAccounts.length > 1) {
            const accounts = currentAccounts.filter((account) => account.tenantId !== '');
            if (accounts.length > 1) {
                const acc = getNewestAccount(accounts);
                // localAccountId identifica l'entitat per a la qual el token afirma informació.
                if (accounts.every((account) => account.localAccountId === acc.localAccountId)) {
                    // Tots els comptes pertanyen al mateix usuari
                    myAccount = accounts.reduce((account, currentAccount) => {
                        if (account.idTokenClaims.auth_time > currentAccount.idTokenClaims.auth_time) {
                            return account;
                        } else {
                            return currentAccount;
                        }
                    });
                } else {
                    // S'han detectat múltiples usuaris. Tancar totes les sessions per seguretat.
                    signOut();
                }
            } else if (accounts.length === 1) {
                myAccount = accounts[0];
            }
        } else if (currentAccounts.length === 1) {
            myAccount = currentAccounts[0];
        }
    }

    setAccount(myAccount);

    return myAccount;
}

const setAccount = (account) => {
    accountId = account?.homeAccountId;
    username = account?.idTokenClaims?.email;
};

function signOut() {
    //TODO: validem q no cal cookie al 3cat per mantenir la sessió
    // const cookieName = '_ccma_usuari_portal_';
    // Cookies.remove(cookieName);
    /**
     * Podeu passar un objecte de sol·licitud personalitzat a continuació. Això substituirà la configuració inicial. Per obtenir més informació, visiteu:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

    // Mode normal amb redirecció configurada a authConfig
    const logoutRequest = {
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
    };

    return myMSALObj.logoutRedirect(logoutRequest);
}

function getTokenRedirect(request) {
    /**
     * Consulteu aquí per obtenir més informació sobre la recuperació de comptes:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */

    const accounts = myMSALObj.getAllAccounts();
    request.account = getNewestAccount(accounts);

    /**
     *
     */
    return myMSALObj
        .acquireTokenSilent(request)
        .then((response) => {
            // En cas que la resposta del servidor B2C tingui un camp accessToken buit
            // llancem un error per iniciar l'adquisició de token
            if (!response.accessToken || response.accessToken === '') {
                throw new InteractionRequiredAuthError();
            } else {
                accessToken = response.accessToken;
                passTokenToApi();
            }
        })
        .catch((error) => {
            if (error instanceof InteractionRequiredAuthError) {
                // recórrer a la interacció quan la crida silenciosa falla
                return myMSALObj.acquireTokenRedirect(request);
            } else {
                console.log(error);
            }
        });
}

function getNewestAccount(accounts) {
    // Validar que accounts existeix i té almenys un element
    if (!accounts || !Array.isArray(accounts) || accounts.length === 0) {
        return null;
    }

    // Si només hi ha un compte, retornar-lo directament
    if (accounts.length === 1) {
        return accounts[0];
    }

    // Obtenir el compte més nou basat en la data d'emissió del token
    const account = accounts.reduce((newest, current) => {
        const newestIat = newest.idTokenClaims?.auth_time || 0;
        const currentIat = current.idTokenClaims?.auth_time || 0;
        return currentIat > newestIat ? current : newest;
    }, accounts[0]);

    return account;
}

function getEmail() {
    const accounts = myMSALObj.getAllAccounts();
    const account = getNewestAccount(accounts);
    return account?.idTokenClaims?.email || null;
}

// Adquireix un token d'accés i després el passa a la crida de l'API
function passTokenToApi() {
    if (!accessToken) {
        getTokenRedirect(tokenRequest);
    } else {
        try {
            callApi(apiConfig.webApi, accessToken);
        } catch (error) {
            console.log(error);
        }
    }
}

/**
 * Per iniciar un flux d'usuari de B2C, simplement feu una sol·licitud d'inici de sessió utilitzant
 * la cadena d'autoritat completa d'aquest flux d'usuari, per exemple
 * https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/B2C_1_edit_profile_v2
 */

function editProfileCP() {
    const editProfileRequest = b2cPolicies.authorities.editProfileCP;
    if (accountId) {
        editProfileRequest.loginHint = getEmail();
        myMSALObj.loginRedirect(editProfileRequest);
    } else {
        signUpSignInCP();
    }
}

function changePassCP() {
    const forgotPasswordRequest = b2cPolicies.authorities.forgotPasswordCP;
    if (accountId) {
        forgotPasswordRequest.loginHint = getEmail();
        myMSALObj.loginRedirect(forgotPasswordRequest);
    } else {
        signUpSignInCP();
    }
}

function changeEmail() {
    const changeEmailRequest = b2cPolicies.authorities.changeEmail;
    if (accountId) {
        changeEmailRequest.loginHint = getEmail();
        myMSALObj.loginRedirect(changeEmailRequest);
    } else {
        signUpSignInCP();
    }
}

function deleteMyAccount() {
    const deleteMyAccountRequest = b2cPolicies.authorities.deleteMyAccount;
    if (accountId) {
        deleteMyAccountRequest.loginHint = getEmail();
        myMSALObj.loginRedirect(deleteMyAccountRequest);
    } else {
        signUpSignInCP();
    }
}

function signUpSignInCP(params = null) {
    const signUpSignInCPRequest = params ? { state: params } : b2cPolicies.authorities.signUpSignInCP;
    myMSALObj.loginRedirect(signUpSignInCPRequest);
}

export {
    getAcquireTokenSilentPromise,
    selectAccount,
    setAccount,
    signUpSignInCP,
    signOut,
    editProfileCP,
    changePassCP,
    changeEmail,
    deleteMyAccount,
    getNewestAccount,
    myMSALObj
};
